import {
  SUBSCRIPTION_PLANS,
  type SubscriptionFeature,
  type SubscriptionPlanCode,
} from "@loyalty/shared";
import type { BusinessSubscription } from "./api";

export interface SubscriptionRequirement {
  feature: SubscriptionFeature;
  label: string;
}

const tenantRouteRequirements: Array<{ prefix: string; requirement: SubscriptionRequirement }> = [
  { prefix: "/tenant/mero-credits", requirement: { feature: "mero_credits", label: "Mero Credits" } },
  { prefix: "/tenant/rewards/campaigns", requirement: { feature: "campaigns", label: "Campaigns" } },
  { prefix: "/tenant/rewards/segments", requirement: { feature: "segments", label: "Customer segments" } },
  { prefix: "/tenant/rewards/automations", requirement: { feature: "automations", label: "Lifecycle automations" } },
  { prefix: "/tenant/rewards/analytics", requirement: { feature: "advanced_analytics", label: "Advanced loyalty analytics" } },
];

export function tenantRouteSubscriptionRequirement(pathname: string): SubscriptionRequirement | null {
  return tenantRouteRequirements.find(({ prefix }) => pathname.startsWith(prefix))?.requirement ?? null;
}

export function subscriptionIncludes(
  subscription: BusinessSubscription,
  feature: SubscriptionFeature,
): boolean {
  return subscription.accessPlan.features.includes(feature);
}

export function firstPlanWithFeature(feature: SubscriptionFeature) {
  return SUBSCRIPTION_PLANS.find((plan) => plan.features.includes(feature)) ?? null;
}

export function nextSubscriptionPlan(code: SubscriptionPlanCode) {
  const index = SUBSCRIPTION_PLANS.findIndex((plan) => plan.code === code);
  return index >= 0 ? (SUBSCRIPTION_PLANS[index + 1] ?? null) : null;
}

export function featureUpgradeReason(
  subscription: BusinessSubscription,
  requirement: SubscriptionRequirement,
) {
  const requiredPlan = firstPlanWithFeature(requirement.feature);
  const currentPlan = subscription.accessPlan;
  return {
    title: `${requirement.label} requires ${requiredPlan?.name ?? "a higher plan"}`,
    message: `${currentPlan.name} does not include ${requirement.label}. Upgrade the business subscription to continue.`,
    currentPlanName: currentPlan.name,
    targetPlanName: requiredPlan?.name ?? "the next plan",
    targetPlanCode: requiredPlan?.code ?? nextSubscriptionPlan(currentPlan.code)?.code ?? null,
  };
}
export function limitUpgradeReason(
  subscription: BusinessSubscription,
  label: string,
  limit: number,
) {
  const currentPlan = subscription.accessPlan;
  const nextPlan = nextSubscriptionPlan(currentPlan.code);
  return {
    title: `${label} limit reached`,
    message: `${currentPlan.name} includes up to ${limit.toLocaleString()} ${label}. Archive or deactivate an existing item, or upgrade the business subscription to continue.`,
    currentPlanName: currentPlan.name,
    targetPlanName: nextPlan?.name ?? "a higher plan",
    targetPlanCode: nextPlan?.code ?? null,
  };
}
